/**
 * Pure helpers for Azure Storage account names / endpoint URLs. Process-agnostic
 * (the `kustoUrl.ts` precedent): imported by the renderer (connection form,
 * dial projection) and the main process (endpoints, credentials). Depends on
 * the shared types only — never on `Connection`, so Task 1.3 -> Task 1.2 only.
 */
import type { AzureStorageEndpoints, StorageService } from '../../types/azureStorage';

/** Azurite's built-in account and published (non-secret) key — Azurite README, verified 2026-09-19. */
export const AZURITE_DEFAULT_ACCOUNT = 'devstoreaccount1';
export const AZURITE_WELL_KNOWN_KEY =
  'Eby8vdM02xNOcqFlqUwJPLlmEtlCDXJ1OUzFT50uSRZ6IFsuFq2UVErCz4I6tq/K1SZFPTOtr/KBHBeksoGMGw==';
export const AZURITE_DEFAULT_HOST = '127.0.0.1';
/** Azurite listens per service; it has no Files or DFS endpoint. */
export const AZURITE_DEFAULT_PORTS = { blob: 10000, queue: 10001, table: 10002 } as const;
export const DEFAULT_ENDPOINT_SUFFIX = 'core.windows.net';

const SERVICE_HOST_LABELS: ReadonlySet<string> = new Set(['blob', 'table', 'queue', 'file', 'dfs']);
const LOOPBACK_HOSTS: ReadonlySet<string> = new Set(['localhost', '::1', '[::1]']);
/** The whole 127.0.0.0/8 block is loopback, not only 127.0.0.1 (a URL normalises `127.1` to `127.0.0.1`). */
const IPV4_LOOPBACK = /^127(?:\.\d{1,3}){3}$/u;

/** `http` is only ever kept for these; every other host is dialled over https. */
export function isLoopbackHost(hostname: string): boolean {
  const host = hostname.toLowerCase();
  return LOOPBACK_HOSTS.has(host) || IPV4_LOOPBACK.test(host);
}

export type StorageAccountInput = {
  accountName?: string;
  /** The endpoint as the user typed it (scheme-normalised, trailing slash trimmed); absent for a bare account name. */
  endpoint?: string;
  /** Azurite-style `http://host:port/<account>` — the account is the first path segment. */
  pathStyle: boolean;
};

/**
 * Interpret the "Account name or endpoint URL" field: a bare account name, a
 * host-style endpoint (`https://<account>.<service>.<suffix>`), or an Azurite
 * path-style URL. Whitespace and a trailing slash are trimmed; a missing scheme
 * defaults to https; `http` survives only on loopback hosts.
 */
export function normalizeStorageAccountInput(input: string): StorageAccountInput {
  const trimmed = input.trim().replace(/\/+$/, '');
  if (!trimmed) return { pathStyle: false };
  if (/^[a-z0-9]{3,24}$/i.test(trimmed)) return { accountName: trimmed, pathStyle: false };
  const withScheme = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  let url: URL;
  try {
    url = new URL(withScheme);
  } catch {
    return { pathStyle: false };
  }
  const segments = url.pathname.split('/').filter(Boolean);
  if (isLoopbackHost(url.hostname)) {
    const accountName = segments[0];
    const endpoint = `${url.protocol}//${url.host}${accountName ? `/${accountName}` : ''}`;
    return accountName ? { accountName, endpoint, pathStyle: true } : { endpoint, pathStyle: true };
  }
  const labels = url.hostname.split('.');
  const accountName = labels.length >= 3 && SERVICE_HOST_LABELS.has(labels[1].toLowerCase()) ? labels[0] : undefined;
  const path = url.pathname.replace(/\/+$/, '');
  const endpoint = `https://${url.host}${path}`;
  return accountName ? { accountName, endpoint, pathStyle: false } : { endpoint, pathStyle: false };
}

export type StorageAccountShape = {
  accountName?: string;
  endpoints?: AzureStorageEndpoints;
  suffix?: string;
  emulator?: boolean;
};

/**
 * The URL a service client is built from: an explicit endpoint verbatim (never
 * re-derived by string replacement), else the Azurite path-style default for the
 * emulator (Blob / Queue / Table only), else `https://<account>.<service>.<suffix>`.
 */
export function serviceEndpointFor(account: StorageAccountShape, service: StorageService | 'dfs'): string | undefined {
  const explicit = account.endpoints?.[service];
  if (explicit) return explicit;
  if (!account.accountName) return undefined;
  if (account.emulator) {
    if (service === 'file' || service === 'dfs') return undefined;
    return `http://${AZURITE_DEFAULT_HOST}:${AZURITE_DEFAULT_PORTS[service]}/${account.accountName}`;
  }
  const suffix = account.suffix?.trim() || DEFAULT_ENDPOINT_SUFFIX;
  return `https://${account.accountName}.${service}.${suffix}`;
}

/**
 * Emulator endpoints — when an account-key connection string is the local emulator (Azurite). ONE rule for every
 * surface that parses such a string: the core parser (`azureStorageConnectionString.ts`) applies it and returns its `development-storage` result,
 * which the dialog's paste (`parseAzureStorage.ts`) and `connections_add` (`addConnectionAzureStorage.ts`) both map to
 * the Emulator (Azurite) preset's shape — so the two cannot drift.
 *
 * Microsoft documents `UseDevelopmentStorage=true` as equivalent to the full string with `BlobEndpoint`, `QueueEndpoint`
 * and `TableEndpoint` on `http://127.0.0.1:10000–10002/devstoreaccount1`; the full string is what people paste when a
 * port, the host or the account differs. Read as an ordinary account string, it left the emulator flag off, so File
 * (and Data Lake) derived `https://<account>.file.core.windows.net` — a real public host — and Test connection sent a
 * request signed with the emulator key there.
 *
 * The rule: a string is the emulator when EVERY endpoint it gives is an emulator endpoint (`isEmulatorEndpoint`: a
 * loopback host — 127.0.0.0/8, `localhost`, `::1` — over http or https, with the account name as the one path segment)
 * and at least one of them is Blob, Queue or Table. Then, exactly as the preset:
 * - the services are those whose endpoint is given, at the given URLs (a remapped port such as 20000 is kept);
 * - no File or Data Lake endpoint exists: Azurite has neither, and an emulator connection never derives or probes one.
 *   A loopback `FileEndpoint` is therefore not used, and is returned in `unusedKeys` (the paste lists it as not used)
 *   together with `EndpointSuffix`, from which nothing is derived for an emulator either;
 * - a string whose only endpoint is a loopback `FileEndpoint` names no service Azurite has, and is refused.
 * A string that mixes an emulator endpoint with any other (a public host, a tunnel without the account path) is NOT the
 * emulator: it names a real account and stays an account string with its endpoints kept verbatim.
 */

/** Azurite's services, in the Emulator (Azurite) preset's order. */
const EMULATOR_SERVICES: readonly StorageService[] = ['blob', 'queue', 'table'];

/** The connection-string keys whose endpoint the rule reads, by service. */
const ENDPOINT_KEY_NAMES: Record<StorageService, string> = {
  blob: 'BlobEndpoint',
  queue: 'QueueEndpoint',
  table: 'TableEndpoint',
  file: 'FileEndpoint',
};

export type EmulatorEndpointsReading =
  | { kind: 'emulator'; endpoints: AzureStorageEndpoints; services: StorageService[]; unusedKeys: string[] }
  | { kind: 'account' }
  | { kind: 'invalid'; reason: string };

/** Azurite's path-style account endpoint: `http(s)://<loopback>[:port]/<account>[/]`, nothing else in the URL. */
export function isEmulatorEndpoint(endpoint: string, accountName: string): boolean {
  let url: URL;
  try {
    url = new URL(endpoint);
  } catch {
    return false;
  }
  if ((url.protocol !== 'http:' && url.protocol !== 'https:') || !isLoopbackHost(url.hostname)) return false;
  if (url.search || url.hash || url.username || url.password) return false;
  const segments = url.pathname.split('/').filter(Boolean);
  return segments.length === 1 && segments[0].toLowerCase() === accountName.toLowerCase();
}

/** Whether an account-key string's explicit endpoints make it the emulator (see the module comment). */
export function readEmulatorEndpoints(accountName: string, given: AzureStorageEndpoints, suffix?: string): EmulatorEndpointsReading {
  const named = (Object.keys(ENDPOINT_KEY_NAMES) as StorageService[]).filter((service) => given[service]);
  if (named.length === 0 || !named.every((service) => isEmulatorEndpoint(given[service] as string, accountName))) return { kind: 'account' };
  const services = EMULATOR_SERVICES.filter((service) => named.includes(service));
  if (services.length === 0) return { kind: 'invalid', reason: 'Azurite has no Files: add a Blob, Queue or Table endpoint' };
  const endpoints: AzureStorageEndpoints = Object.fromEntries(services.map((service) => [service, given[service]]));
  const unusedKeys = [...(given.file ? [ENDPOINT_KEY_NAMES.file] : []), ...(suffix ? ['EndpointSuffix'] : [])];
  return { kind: 'emulator', endpoints, services, unusedKeys };
}
